const SAFE_URL = /^https:\/\/[^\s<>]+$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9][0-9()\s.-]{6,}[0-9]$/;

export function normalizeRichMessage(value = "") {
  return String(value)
    .replace(/\r\n?/g, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\u0000/g, "");
}

export function selectionRange(value = "", start = 0, end = start) {
  const text = String(value);
  return {
    start: Math.max(0, Math.min(Number(start) || 0, text.length)),
    end: Math.max(0, Math.min(Number(end) || 0, text.length)),
  };
}

function selectionIsInside(value, start, end, expression, contentOffset = 0) {
  const source = String(value);
  for (const match of source.matchAll(expression)) {
    const matchStart = Number(match.index || 0);
    const contentStart = matchStart + contentOffset;
    const contentEnd = contentStart + String(match[1] || "").length;
    if (start >= contentStart && end <= contentEnd) return true;
  }
  return false;
}

/** The selection/caret is the authority for toolbar active state, not click history. */
export function formattingStateAtSelection(value = "", start = 0, end = start) {
  const source = normalizeRichMessage(value);
  const range = selectionRange(source, start, end);
  const lineStart = source.lastIndexOf("\n", Math.max(0, range.start - 1)) + 1;
  const lineEndIndex = source.indexOf("\n", range.start);
  const line = source.slice(lineStart, lineEndIndex === -1 ? source.length : lineEndIndex);
  return {
    bold: selectionIsInside(source, range.start, range.end, /\*\*([^*\n]+)\*\*/g, 2),
    italic: selectionIsInside(source, range.start, range.end, /(?:^|[^*])\*([^*\n]+)\*(?!\*)/gm, 1),
    bullet: /^\s*[-*]\s+/.test(line),
    numbered: /^\s*\d+[.)]\s+/.test(line),
    link: selectionIsInside(source, range.start, range.end, /\[([^\]\n]+)\]\(https:\/\/[^)\s]+\)/g, 1),
  };
}

export function wrapSelection(value, start, end, before, after = before) {
  const text = normalizeRichMessage(value);
  const range = selectionRange(text, start, end);
  const selected = text.slice(range.start, range.end) || "text";
  return {
    value: `${text.slice(0, range.start)}${before}${selected}${after}${text.slice(range.end)}`,
    selectionStart: range.start + before.length,
    selectionEnd: range.start + before.length + selected.length,
  };
}

export function prefixSelectedLines(value, start, end, prefix) {
  const text = normalizeRichMessage(value);
  const range = selectionRange(text, start, end);
  const lineStart = text.lastIndexOf("\n", Math.max(0, range.start - 1)) + 1;
  const lineEndIndex = text.indexOf("\n", range.end);
  const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
  const selectedLines = text.slice(lineStart, lineEnd).split("\n");
  const nextLines = selectedLines.map((line, index) => {
    if (/^\s*$/.test(line)) return prefix.trimEnd();
    return prefix === "1. " ? `${index + 1}. ${line.replace(/^\d+[.)]\s+/, "")}` : `${prefix}${line.replace(/^[*-]\s+/, "")}`;
  });
  const replacement = nextLines.join("\n");
  return {
    value: `${text.slice(0, lineStart)}${replacement}${text.slice(lineEnd)}`,
    selectionStart: lineStart,
    selectionEnd: lineStart + replacement.length,
  };
}

export function continueListOnEnter(value, caret) {
  const text = normalizeRichMessage(value);
  const index = Math.max(0, Math.min(Number(caret) || 0, text.length));
  const lineStart = text.lastIndexOf("\n", Math.max(0, index - 1)) + 1;
  const line = text.slice(lineStart, index);
  const bullet = line.match(/^\s*[-*]\s+(.*)$/);
  const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
  if (!bullet && !numbered) return null;
  if (bullet && !bullet[1].trim()) {
    return { value: `${text.slice(0, lineStart)}${text.slice(index)}`, caret: lineStart };
  }
  if (numbered && !numbered[2].trim()) {
    return { value: `${text.slice(0, lineStart)}${text.slice(index)}`, caret: lineStart };
  }
  const prefix = bullet ? "- " : `${Number(numbered[1]) + 1}. `;
  return { value: `${text.slice(0, index)}\n${prefix}${text.slice(index)}`, caret: index + prefix.length + 1 };
}

export function isSafeExternalUrl(value) {
  return SAFE_URL.test(String(value || "").replace(/[),.!?]+$/, ""));
}

export function safeHref(value) {
  const candidate = String(value || "").replace(/[),.!?]+$/, "");
  return isSafeExternalUrl(candidate) ? candidate : "";
}

export function entityForToken(value) {
  const raw = String(value || "");
  const url = safeHref(raw);
  if (url) return { type: "url", value: url, href: url };
  if (EMAIL.test(raw)) return { type: "email", value: raw, href: `mailto:${raw}` };
  if (PHONE.test(raw)) {
    const normalized = raw.replace(/[\s().-]/g, "");
    return { type: "phone", value: raw, href: `tel:${normalized}` };
  }
  return null;
}

export function findUnambiguousDates(value) {
  const text = normalizeRichMessage(value);
  const matches = [];
  const pattern = /\b(?:\d{1,2}\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)(?:\s+\d{4})?|(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2}(?:,?\s+\d{4})?)(?:\s+(?:at\s+)?\d{1,2}:\d{2}(?:\s?[ap]m)?)?\b/gi;
  for (const match of text.matchAll(pattern)) matches.push({ value: match[0], index: match.index ?? 0 });
  return matches;
}
